<script lang="ts">
import type { AnyItem, CollectionCodeDataItem, CompositePrimaryKey, GlobalCollectionId, GlobalFieldId } from '#layers/data-model/public';
import type { FormProps as FormCompProps } from '#layers/ui/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed, defineAsyncComponent } from 'vue';
import { LazyCollectionSelect } from '#layers/data-model/lazy';

export type { FormBlockSuccessEventPayload } from '#layers/blocks/types/form-block';

const LazyFormEjectControl = defineAsyncComponent(() => import('#layers/blocks/components/Form/FormEjectControl.vue'));

const __DsBlock = {
	type: 'container',
	name: '$t:blocks.form.name',
	description: '$t:blocks.form.description',
	category: 'basic',
	icon: 'lucide:square-pen',
	events: [
		{
			name: 'submit',
			payload: [
				{
					name: 'event',
					type: 'object',
					array: false,
					required: true,
				},
			],
		},
		{
			name: 'success',
			payload: [
				{
					name: 'event',
					type: 'object',
					array: false,
					required: true,
				},
			],
		},
	],
	slots: ({ props, ...context }) => [
		...!(
			(props.collectionId && !context.getRootContext().collectionId)
			|| blockContextIsDetailPage(context)
		) || props.ejected
			? [
					{
						name: 'default',
						supportedBlocks: (b) => !!b.tags?.includes('form-container'),
					} as BlockSlotDefinition,
				]
			: [],
		...!blockContextIsDetailPage(context)
			? [
					{
						name: 'actions',
					},
				]
			: [],
	],

	options: ({ form, values, context }) => form([
		{
			name: 'collectionId',
			label: '$t:blocks.common.props.collection',
			render: LazyCollectionSelect,
			hidden: () => blockContextIsDetailPage(context),
		},

		{
			name: 'itemId',
			label: '$t:blocks.common.props.item',
			hidden: () => !(values.collectionId || context.getRootContext().collectionId) || blockContextIsDetailPage(context),
		},

		{
			name: 'ejected',
			label: '$t:blocks.form.auto_generate_fields',
			hidden: () => !(values.collectionId || context.getRootContext().collectionId),
			render: LazyFormEjectControl,
		},

		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
			props: {
				label: '$t:blocks.common.groups.appearance',
			},
		},

		{
			name: 'layout',
			type: 'enum',
			values: ['stacked', 'side-by-side'],
			label: '$t:blocks.form.layout',
			description: '$t:blocks.form.layout_description',
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				options: [
					{
						label: '$t:blocks.form.stacked',
						value: 'stacked',
						icon: 'lucide:rows-2',
					},
					{
						label: '$t:blocks.form.side_by_side',
						value: 'side-by-side',
						icon: 'lucide:columns-2',
					},
				],
			},
		},

		{
			name: 'layoutSplit',
			type: 'enum',
			values: ['1-1', '1-2'],
			label: '$t:blocks.form.layout_split',
			description: '$t:blocks.form.layout_split_description',
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				options: [
					{
						label: '$t:blocks.form.label_narrow',
						value: '1-2',
						icon: 'lucide:panel-left',
					},
					{
						label: '$t:blocks.form.label_wide',
						value: '1-1',
						icon: 'lucide:columns-2',
					},
				],
			},
			hidden: () => values.layout === 'stacked',
		},

		{
			name: 'submitLabel',
			label: '$t:blocks.form.props.submit_label',
			hidden: () => blockContextIsDetailPage(context),
		},

		{
			name: 'submitIcon',
			label: '$t:blocks.form.props.submit_icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
			hidden: () => blockContextIsDetailPage(context),
		},
	]),

	locked: blockContextIsDetailPage,

	hidden: blockContextIsContentPage,
}</script>

<script lang="ts" setup>
import type { BlockSlotDefinition } from '#layers/block-engine/public';
import type { FormBlockSuccessEventPayload } from '#layers/blocks/types/form-block';
import { isNullishOrEmpty } from '#layers/base/public';
import { blockContextIsContentPage, blockContextIsDetailPage, defineBlock, FormSchemaKind, useBlockInEditorContext } from '#layers/block-engine/public';
import { exposeFormToBlockRootContext } from '#layers/blocks/composables/block-context/form-root-context';
import { useFormBlock } from '#layers/blocks/composables/form-block/use-form-block';
import { toJavascriptSerializable } from '#layers/data-model/public';
import { ErrorAlert, OverflowMenu, TeleportPageActions } from '#layers/ui/public';

interface SubmitEvent {
	data: AnyItem;
}

interface FormProps {
	/**
	 * Link this form to a collection.
	 */
	collectionId?: GlobalCollectionId;
	/**
	 * Identifier of the item to edit. Leave blank or use + to create an item.
	 */
	itemId?: string | CompositePrimaryKey;
	layout?: FormCompProps['layout'];
	layoutSplit?: FormCompProps['layoutSplit'];
	submitLabel?: string;
	submitIcon?: string;
	resetOnCreate?: boolean;
	/**
	 * Override the fields.
	 */
	fields?: GlobalFieldId[];
	/**
	 * By default the form automatically generates inputs from the collection fields. You can eject the form to customize it.
	 */
	ejected?: boolean;
}

const props = withDefaults(defineProps<FormProps>(), {
	layout: 'stacked',
	layoutSplit: '1-1',
});

const emit = defineEmits<{
	submit: [event: SubmitEvent];
	success: [event: FormBlockSuccessEventPayload];
}>();

;

const { blockInEditor, slotIsEmpty } = useBlockInEditorContext();

const {
	formId,
	formObject,
	isUpdate,
	finalCollectionId,
	currentItemId,
	collectionData,
	computedFields,
	isDetailPageForm,
	initError,
	onSubmit,
	showSubmit,
	submitDisabled,
	submitError,
	saveHandlerErrors,
	overflowActions,
	getFieldOverride,
	formDisabled,
	isFieldDisabled,
	getFieldDisabledNotice,
} = useFormBlock({
	collectionId: () => props.collectionId,
	itemId: () => props.itemId,
	fields: () => props.fields,
	resetOnCreate: () => props.resetOnCreate,
	blockInEditor: () => blockInEditor,
	onSuccess: (payload) => emit('success', payload),
	onSubmit: (event) => emit('submit', event),
});

const currentValues = computed<CollectionCodeDataItem | undefined>(() => {
	const currentFormObject = formObject.value;

	if (!currentFormObject) {
		return;
	}

	const values: CollectionCodeDataItem = {};

	for (const [key, value] of Object.entries(currentFormObject)) {
		if (!key.startsWith('$')) {
			values[key] = toJavascriptSerializable(value);
		}
	}

	return values;
});

exposeFormToBlockRootContext(() => ({
	...(collectionData.value ? { collection: collectionData.value } : {}),
	fields: computedFields.value,
	currentItemId: currentItemId.value,
	...(currentValues.value ? { currentValues: currentValues.value } : {}),
}));
</script>

<template>
	<div
		class="flex flex-col gap-4"
		:class="{
			'@w-lg:rounded-sm @w-lg:border @w-lg:p-4': !isDetailPageForm,
			'mx-auto w-page pt-4': isDetailPageForm,
		}"
	>
		<ErrorAlert :error="[initError, submitError, ...saveHandlerErrors]" />

		<FormCustomizationMissingFieldsIndicator
			v-if="ejected && isDetailPageForm && collectionData && !blockInEditor"
			:collection="collectionData"
		/>

		<DForm
			v-if="formObject"
			:id="formId"
			:state="formObject"
			:schema="formObject.$schema"
			:layout
			:layout-split
			:disabled="formDisabled"
			auto-focus-first-input
			@submit="onSubmit"
		>
			<template #content-top>
				<template v-if="(!finalCollectionId || ejected) && blockInEditor && slotIsEmpty()">
					<DEmpty
						:description="$t('blocks.form.empty')"
						icon="lucide:square-pen"
					/>
				</template>
			</template>

			<template v-if="!finalCollectionId || ejected">
				<slot />
			</template>

			<template v-else-if="collectionData">
				<template
					v-for="field of computedFields"
					:key="field.id"
				>
					<BlockRender
						v-if="!getFieldOverride(field.id, 'hidden')"
						block="$builtin:FormField"
						:props="{
							fieldId: field.id,
							disabled: getFieldOverride(field.id, 'disabled') || isFieldDisabled(field.id),
							disabledNotice: getFieldDisabledNotice(field.id),
						}"
					/>
				</template>
			</template>

			<template #content-bottom>
				<div class="flex justify-end pt-6">
					<TeleportPageActions :disabled="!isDetailPageForm">
						<div class="flex items-center gap-2">
							<BlockMarkHorizontalLayout>
								<slot name="actions" />
							</BlockMarkHorizontalLayout>

							<OverflowMenu
								v-if="!isNullishOrEmpty(overflowActions)"
								:items="overflowActions"
								:ui="{
									content: 'min-w-48',
								}"
								:trigger="{
									'data-testid': 'form-overflow-menu',
									'aria-label': $t('blocks.form.form_menu_label'),
									'variant': 'outline',
									'size': 'sm',
									'class': 'text-default',
								}"
							/>

							<DButton
								v-if="showSubmit"
								data-testid="form-submit"
								type="submit"
								:form="formId"
								:label="submitLabel || ((finalCollectionId && !isUpdate) ? $t('common.create') : $t('common.save'))"
								:icon="submitIcon"
								:loading="formObject.$loading"
								:disabled="submitDisabled"
								size="sm"
								leading-icon="lucide:check"
							/>
						</div>
					</TeleportPageActions>
				</div>
			</template>
		</DForm>
	</div>
</template>
